import React, { useState, useRef, useCallback, useEffect } from 'react';
import Icon from './AppIcon';

const WORDS_PER_MINUTE = 150;
const CHUNK_SIZE = 1400; // characters per utterance — larger chunks = fewer pauses between speech

/**
 * Recursively extract plain text from a React element tree.
 * Skips script, style, and code blocks for cleaner TTS.
 */
function extractTextFromContent(element) {
  if (!element) return '';
  if (typeof element === 'string') return element.replace(/\s+/g, ' ').trim();
  if (Array.isArray(element)) return element.map(extractTextFromContent).join(' ');
  if (!React.isValidElement(element)) return '';
  const { type, props } = element;
  const tag = typeof type === 'string' ? type : '';
  if (['script', 'style', 'code', 'pre'].includes(tag)) return '';
  const children = React.Children.toArray(props?.children ?? []);
  return children.map(extractTextFromContent).join(' ');
}

/**
 * Estimate listening time in minutes from word count.
 */
function getListeningMinutes(wordCount) {
  if (!wordCount) return 0;
  return Math.max(1, Math.ceil(wordCount / WORDS_PER_MINUTE));
}

/**
 * Split text into chunks for SpeechSynthesis (avoids long-utterance issues).
 */
function chunkText(text, size = CHUNK_SIZE) {
  const chunks = [];
  let start = 0;
  while (start < text.length) {
    let end = Math.min(start + size, text.length);
    if (end < text.length) {
      const lastSpace = text.lastIndexOf(' ', end);
      if (lastSpace > start) end = lastSpace;
    }
    const chunk = text.slice(start, end).trim();
    if (chunk) chunks.push(chunk);
    start = end;
  }
  return chunks;
}

/** Keywords that often indicate a premium / natural voice (names vary by OS and browser). */
const PREMIUM_VOICE_KEYWORDS = ['natural', 'premium', 'enhanced', 'google', 'samantha', 'daniel', 'karen', 'alex', 'moira', 'siri', 'zira', 'aria', 'neural', 'wave'];

/**
 * Pick the best available voice: prefer on-device (localService), English, and names suggesting quality.
 */
function selectBestVoice() {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
  if (!synth) return null;
  const voices = synth.getVoices();
  if (!voices.length) return null;

  const enVoices = voices.filter((v) => (v.lang || '').startsWith('en'));
  const candidates = enVoices.length ? enVoices : voices;

  const byScore = (v) => {
    let score = 0;
    if (v.localService) score += 100; // on-device often better
    const name = (v.name || '').toLowerCase();
    if (PREMIUM_VOICE_KEYWORDS.some((k) => name.includes(k))) score += 50;
    if ((v.lang || '').startsWith('en-US')) score += 20;
    if ((v.lang || '').startsWith('en')) score += 10;
    return score;
  };

  const sorted = [...candidates].sort((a, b) => byScore(b) - byScore(a));
  return sorted[0] || voices[0];
}

function ListenToArticle({ content, title = '', shortDescription = '', className = '' }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [supported, setSupported] = useState(true);
  const chunksRef = useRef([]);
  const currentIndexRef = useRef(0);
  const utteranceRef = useRef(null);
  const selectedVoiceRef = useRef(null);

  const fullText = React.useMemo(() => {
    const raw = [title, shortDescription, extractTextFromContent(content)]
      .filter(Boolean)
      .join('. ');
    return raw.replace(/\s+/g, ' ').trim();
  }, [content, title, shortDescription]);

  const wordCount = fullText ? fullText.split(/\s+/).length : 0;
  const listeningMinutes = getListeningMinutes(wordCount);

  const speakNext = useCallback(() => {
    if (currentIndexRef.current >= chunksRef.current.length) {
      setIsPlaying(false);
      return;
    }
    const synth = window.speechSynthesis;
    if (synth.speaking) return;
    const text = chunksRef.current[currentIndexRef.current];
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.7; // slower than default (1) for clearer, easier listening
    if (selectedVoiceRef.current) u.voice = selectedVoiceRef.current;
    u.onend = () => {
      currentIndexRef.current += 1;
      speakNext();
    };
    u.onerror = () => {
      setIsPlaying(false);
    };
    utteranceRef.current = u;
    synth.speak(u);
  }, []);

  const play = useCallback(() => {
    if (!window.speechSynthesis) {
      setSupported(false);
      return;
    }
    if (isPlaying) return;
    if (!fullText) return;
    if (!selectedVoiceRef.current) selectedVoiceRef.current = selectBestVoice();
    chunksRef.current = chunkText(fullText);
    currentIndexRef.current = 0;
    setIsPlaying(true);
    speakNext();
  }, [fullText, isPlaying, speakNext]);

  const pause = useCallback(() => {
    window.speechSynthesis?.cancel();
    setIsPlaying(false);
    utteranceRef.current = null;
  }, []);

  useEffect(() => {
    const synth = window.speechSynthesis;
    const onVoicesChanged = () => {
      if (!selectedVoiceRef.current) selectedVoiceRef.current = selectBestVoice();
    };
    if (synth) {
      selectedVoiceRef.current = selectBestVoice();
      synth.addEventListener('voiceschanged', onVoicesChanged);
      return () => {
        synth.removeEventListener('voiceschanged', onVoicesChanged);
        synth.cancel();
      };
    }
    return () => synth?.cancel();
  }, []);

  if (!fullText) return null;

  return (
    <div className={className}>
      <div
        className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-[#00d690]/15 p-4 sm:p-5 flex items-center gap-4 sm:gap-5"
        role="region"
        aria-label="Listen to article"
      >
        <button
          type="button"
          onClick={isPlaying ? pause : play}
          disabled={!supported}
          className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#00d690] text-white flex items-center justify-center hover:bg-[#00b876] focus:outline-none focus:ring-2 focus:ring-[#00d690] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <Icon name="Pause" size={24} className="sm:w-7 sm:h-7" aria-hidden />
          ) : (
            <Icon name="Play" size={24} className="sm:w-7 sm:h-7 ml-0.5" aria-hidden />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-base sm:text-lg font-medium text-[#0f172a]">
            Listen to article
          </p>
          <p className="text-sm sm:text-base text-[#00d690]/80 mt-0.5">
            {listeningMinutes} {listeningMinutes === 1 ? 'minute' : 'minutes'}
          </p>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* Decorative waveform-like bars */}
          <div className="hidden sm:flex items-end gap-0.5 h-6" aria-hidden>
            {[0.4, 0.7, 1, 0.6, 0.9, 0.5, 0.8].map((h, i) => (
              <div
                key={i}
                className={`w-1 rounded-full bg-[#00d690]/40 ${isPlaying ? 'animate-pulse bg-[#00d690]/70' : ''}`}
                style={{ height: `${h * 100}%`, minHeight: 4 }}
              />
            ))}
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowInfo((s) => !s)}
              className="w-8 h-8 rounded-full bg-[#00d690]/15 text-[#00d690] flex items-center justify-center hover:bg-[#00d690]/25 focus:outline-none focus:ring-2 focus:ring-[#00d690] focus:ring-offset-2 transition-colors"
              aria-label="About listen feature"
              aria-expanded={showInfo}
            >
              <Icon name="Info" size={16} aria-hidden />
            </button>
            {showInfo && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden
                  onClick={() => setShowInfo(false)}
                />
                <div
                  className="absolute right-0 top-full mt-2 z-20 w-64 sm:w-72 p-3 bg-white border border-[#00d690]/20 text-[#0f172a] text-xs sm:text-sm rounded-lg shadow-lg"
                  role="tooltip"
                >
                  This uses your browser’s built-in text-to-speech. Quality and voices depend on your device and language settings. Great for accessibility and listening on the go.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {!supported && (
        <p className="mt-2 text-sm text-[#0f172a]/80">
          Your browser doesn’t support text-to-speech. Try Chrome, Edge, or Safari.
        </p>
      )}
    </div>
  );
}

export default ListenToArticle;
